import React, { useState, useEffect, useCallback } from 'react';
import { Coins, Check, X } from 'lucide-react';
import { getCreditRequests, approveCreditRequest, declineCreditRequest } from '../api';
import { useOrg } from '../contexts/OrgContext';
import { groupByDay } from '../utils/groupByDay';

const NO_SPIN = '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
const money = (n) => `$${Math.max(Number(n) || 0, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const initialsOf = (name, email) => {
  const src = (name || email || 'U').trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || 'U') + (parts[1]?.[0] || '')).toUpperCase();
};

function Avatar({ name, email }) {
  return (
    <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-orange/10 text-orange text-sm font-display font-bold shrink-0">
      {initialsOf(name, email)}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-field" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-40 bg-field rounded" />
          <div className="h-2.5 w-56 bg-field rounded" />
        </div>
        <div className="h-8 w-24 bg-field rounded-lg" />
      </div>
    </div>
  );
}

function RequestCard({ req, busy, onAdd, onDecline }) {
  const used = req.current_limit != null ? Math.max(req.current_limit - (req.current_remaining ?? 0), 0) : 0;

  return (
    <div className="group bg-panel border border-panel-border rounded-xl p-4 transition-colors hover:border-fg/15">
      <div className="flex items-start gap-3">
        <Avatar name={req.name} email={req.email} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm text-fg font-medium truncate">{req.name || req.email || req.user_id}</span>
                {req.current_limit != null ? (
                  <span className="text-xs text-fg-muted tabular-nums">Used {money(used)} ({money(req.current_limit)})</span>
                ) : (
                  <span className="text-xs text-fg-muted">No personal limit yet</span>
                )}
              </div>
              {req.name && req.email && <div className="text-xs text-fg-muted truncate">{req.email}</div>}
            </div>
            {req.created_at && (
              <span className="text-xs text-fg-muted shrink-0 tabular-nums">{timeOf(req.created_at)}</span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-3">
            {req.message ? (
              <p className="flex-1 min-w-0 text-sm text-fg-muted whitespace-pre-wrap break-words">{req.message}</p>
            ) : (
              <div className="flex-1" />
            )}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => onAdd(req)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange/15 text-orange rounded-lg text-sm font-medium hover:bg-orange/25 disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> Add credits
              </button>
              <button
                onClick={() => onDecline(req)}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/25 transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" /> Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreditRequests() {
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [approving, setApproving] = useState(null); // the request being approved (modal)
  const [amount, setAmount] = useState('');

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getCreditRequests(orgId);
      setRequests(data.requests || []);
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to load requests' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  // Open the modal, pre-filling the current limit as a starting point.
  const openAdd = (req) => {
    setAmount(req.current_limit != null ? String(req.current_limit) : '');
    setApproving(req);
  };

  const grant = async () => {
    const value = parseFloat(amount);
    if (isNaN(value) || value < 0) return;
    setBusy(true); setMsg(null);
    try {
      await approveCreditRequest(orgId, approving.request_id, value);
      setRequests((rs) => rs.filter((r) => r.request_id !== approving.request_id));
      setMsg({ kind: 'ok', text: `Granted ${money(value)} to ${approving.name || approving.email}.` });
      setApproving(null); setAmount('');
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to grant credits' });
    } finally { setBusy(false); }
  };

  const decline = async (req) => {
    setBusy(true); setMsg(null);
    try {
      await declineCreditRequest(orgId, req.request_id);
      setRequests((rs) => rs.filter((r) => r.request_id !== req.request_id));
      setMsg({ kind: 'ok', text: `Declined the request from ${req.name || req.email}.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to decline' });
    } finally { setBusy(false); }
  };

  const groups = groupByDay(requests, 'created_at');
  const canGrant = amount.trim() !== '' && !isNaN(parseFloat(amount)) && parseFloat(amount) >= 0;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <Coins className="w-6 h-6 text-orange" /> Credit Requests
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          User requests for credits will appear here.
        </p>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg border text-sm flex items-center justify-between ${
          msg.kind === 'ok' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                            : 'bg-red-500/10 border-red-500/25 text-red-400'}`}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-panel border border-panel-border rounded-2xl p-12 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange/10 text-orange mb-4">
            <Coins className="w-7 h-7" />
          </div>
          <div className="text-fg font-medium">No credit requests</div>
          <p className="text-fg-muted text-sm mt-1 max-w-sm mx-auto">
            When user requests for more credit, it'll appear here you can add credits or decline the request.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{g.label}</span>
                <span className="h-px flex-1 bg-panel-border" />
                <span className="text-xs text-fg-muted tabular-nums">{g.items.length}</span>
              </div>
              <div className="space-y-2">
                {g.items.map((req) => (
                  <RequestCard key={req.request_id} req={req} busy={busy} onAdd={openAdd} onDecline={decline} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add credits modal */}
      {approving && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !busy && setApproving(null)}>
          <div className="w-full max-w-md bg-panel border border-panel-border rounded-2xl p-6 shadow-2xl shadow-black/40"
            onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="add-credits-title">
            <div className="flex items-center justify-between mb-1">
              <h2 id="add-credits-title" className="font-display text-lg font-bold text-fg flex items-center gap-2">
                <Coins className="w-5 h-5 text-orange" /> Add credits
              </h2>
              <button onClick={() => !busy && setApproving(null)} className="text-fg-muted hover:text-fg"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-fg-muted mb-4">
              Set Usage limit for <span className="text-fg">{approving.name || approving.email}</span>.
            </p>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">New limit</label>
            <div className="relative mb-5">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted text-sm pointer-events-none">$</span>
              <input
                autoFocus
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canGrant) grant(); }}
                placeholder="0.00"
                className={`w-full pl-6 pr-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm tabular-nums focus:border-orange focus:outline-none ${NO_SPIN}`}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setApproving(null)} disabled={busy}
                className="px-4 py-2 bg-field border border-field-border text-fg-muted hover:text-fg hover:border-orange rounded-lg text-sm transition-colors">
                Cancel
              </button>
              <button onClick={grant} disabled={busy || !canGrant}
                className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {busy ? 'Granting…' : 'Grant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
